import { format, parseISO } from 'date-fns';

/**
 * Helpers for worker timesheet daily hour rows (calendar month grid/list).
 *
 * Internal field `_rowTouched` is UI-only and must be stripped before API submission.
 */

/**
 * Parses a numeric hour value from form input (number or string).
 *
 * @param {number|string|null|undefined} v
 * @returns {number}
 */
export function coerceHourValue(v) {
  if (v === '' || v == null) return 0;
  const n = typeof v === 'number' ? v : parseFloat(String(v).replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
}

/**
 * Sums the six hour columns for one daily row (base, daycare, meeting,
 * training, variable, transport). A day with only variable or only transport
 * hours still counts as worked.
 *
 * @param {Object} row
 * @returns {number}
 */
export function sumDayHours(row) {
  if (!row) return 0;
  return (
    coerceHourValue(row.regular_hours) +
    coerceHourValue(row.daycare_hours) +
    coerceHourValue(row.meeting_hours) +
    coerceHourValue(row.training_hours) +
    coerceHourValue(row.variable_hours) +
    coerceHourValue(row.transport_hours)
  );
}

/**
 * Builds one row per calendar day for the given `YYYY-MM` month,
 * excluding Friday and Saturday (`work_date` remains ISO `YYYY-MM-DD` for the API).
 *
 * @param {string} yyyyMm - e.g. `2026-05`
 * @returns {Array<Object>} Rows with `work_date`, hour fields, day-status fields, and `_rowTouched`
 */
export function buildDailyHoursForMonth(yyyyMm) {
  if (!yyyyMm || !/^\d{4}-\d{2}$/.test(yyyyMm)) {
    return [];
  }
  const [y, m] = yyyyMm.split('-').map(Number);
  const daysInMonth = new Date(y, m, 0).getDate();
  const rows = [];
  for (let d = 1; d <= daysInMonth; d += 1) {
    const dow = new Date(y, m - 1, d).getDay();
    if (dow === 5 || dow === 6) continue;
    const dateStr = `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    rows.push({
      work_date: dateStr,
      regular_hours: 0,
      daycare_hours: 0,
      meeting_hours: 0,
      training_hours: 0,
      variable_hours: 0,
      transport_hours: 0,
      is_sick_day: false,
      is_off_day: false,
      sick_note_frontend_id: '',
      sick_note_file: null,
      sick_note_filename: '',
      sick_note_content_type: '',
      sick_note_size: 0,
      _rowTouched: false,
    });
  }
  return rows;
}

/**
 * Formats a stored ISO calendar date (`YYYY-MM-DD`) for UI as DD/MM/YYYY.
 *
 * @param {string} yyyyMmDd
 * @returns {string}
 */
export function formatWorkDateDisplay(yyyyMmDd) {
  if (!yyyyMmDd) return '';
  try {
    return format(parseISO(yyyyMmDd), 'dd/MM/yyyy');
  } catch {
    return yyyyMmDd;
  }
}

/**
 * Returns true if any hour cell was edited or any hour is non-zero.
 *
 * @param {Object} row
 * @returns {boolean}
 */
export function rowShouldSubmit(row) {
  if (!row) return false;
  if (row._rowTouched) return true;
  return sumDayHours(row) > 0;
}

/**
 * Maps internal rows to API `daily_hours` entries (no `_rowTouched`).
 *
 * @param {Object[]} rows
 * @returns {Object[]}
 */
export function toPayloadDailyHours(rows) {
  return (rows || [])
    .filter(row => rowShouldSubmit(row))
    .map(row => {
      const hasSickNote = Boolean(
        row.sick_note_frontend_id &&
          row.sick_note_filename &&
          row.sick_note_content_type &&
          Number(row.sick_note_size) > 0,
      );
      return {
        work_date: row.work_date,
        regular_hours: coerceHourValue(row.regular_hours),
        daycare_hours: coerceHourValue(row.daycare_hours),
        meeting_hours: coerceHourValue(row.meeting_hours),
        training_hours: coerceHourValue(row.training_hours),
        variable_hours: coerceHourValue(row.variable_hours),
        transport_hours: coerceHourValue(row.transport_hours),
        is_sick_day: Boolean(row.is_sick_day),
        is_off_day: Boolean(row.is_off_day),
        has_sick_note: hasSickNote,
        sick_note_frontend_id: hasSickNote ? row.sick_note_frontend_id : undefined,
        sick_note_filename: hasSickNote ? row.sick_note_filename : undefined,
        sick_note_content_type: hasSickNote
          ? row.sick_note_content_type
          : undefined,
        sick_note_size: hasSickNote ? Number(row.sick_note_size) : undefined,
      };
    });
}

/**
 * Column totals for footer summaries.
 *
 * @param {Object[]} rows
 * @returns {{ regular: number, daycare: number, meeting: number, training: number, variable: number, transport: number, total: number }}
 */
export function sumColumns(rows) {
  const acc = {
    regular: 0,
    daycare: 0,
    meeting: 0,
    training: 0,
    variable: 0,
    transport: 0,
    total: 0,
  };
  (rows || []).forEach(row => {
    const r = coerceHourValue(row.regular_hours);
    const dc = coerceHourValue(row.daycare_hours);
    const mt = coerceHourValue(row.meeting_hours);
    const tr = coerceHourValue(row.training_hours);
    const vr = coerceHourValue(row.variable_hours);
    const tp = coerceHourValue(row.transport_hours);
    acc.regular += r;
    acc.daycare += dc;
    acc.meeting += mt;
    acc.training += tr;
    acc.variable += vr;
    acc.transport += tp;
    acc.total += r + dc + mt + tr + vr + tp;
  });
  return acc;
}
